import * as React from "react";
import {useId, useState} from "react";
import {Modal} from "@/shared/ui/primitives/reactBootstrapModal";
import {DatePickerFormGroup} from "@/shared/ui/form/DatePickerFormGroup";
import {FormGroup} from "@/shared/ui/form/FormGroup";
import {CreateAnotherCheckbox} from "@/shared/ui/form/CreateAnotherCheckbox";
import {WorkingTimeTypeSelect} from "@/shared/ui/primitives/WorkingTimeTypeSelect";
import type {WorkingTimeNotice, WorkingTimeNoticeRequestResponseModel, WorkingTimeType} from "@/shared/timr";
import {WorkingTimeNoticeMode} from "@/shared/timrTypes";
import type {
	WorkingTimeNoticeDialogControllerOptions,
	WorkingTimeNoticeForm
} from "@/features/requests/workingTimeNoticeDialog/workingTimeNoticeDialogController";
import {
	isNoticeRequestForm,
	useWorkingTimeNoticeDialogContext,
	useWorkingTimeNoticeDialogController,
	WorkingTimeNoticeDialogProvider
} from "@/features/requests/workingTimeNoticeDialog/workingTimeNoticeDialogController";
import {permissionForSaveAndApproveButton} from "@/shared/securityHelper";
import {
	WorkingTimeNoticeUserComboBoxFormGroup
} from "@/features/requests/workingTimeNoticeDialog/workingTimeNoticeUserComboBoxFormGroup";
import {
	WorkingTimeNoticeRevisions
} from "@/features/requests/workingTimeNoticeDialog/workingTimeNoticeRevisions";
import type {UserOption} from "@/features/requests/workingTimeNoticeDialog/useNoticeUserOptions";
import {parseISODate} from "@/shared/utils/datefunctions";
import {Temporal} from "@js-temporal/polyfill";

// The common props are the controller's options minus the bits each variant supplies itself: the
// initial form, `readOnly` (which the edit variant derives — see `isNoticeReadOnly` — and create and
// duplicate hard-code to false), `userSelectable` (create and duplicate allow picking the user, edit
// fixes it), and `createAnother` (only create offers the checkbox that drives it).
type WorkingTimeNoticeDialogCommonProps = Omit<
	WorkingTimeNoticeDialogControllerOptions,
	"initialForm" | "readOnly" | "userSelectable" | "createAnother"
>;

export interface CreateWorkingTimeNoticeInitialValues {
	selectedUser?: UserOption | null;
	start?: Temporal.PlainDate | null;
	end?: Temporal.PlainDate | null;
}

type EditWorkingTimeNoticeDialogProps = WorkingTimeNoticeDialogCommonProps & {
	notice: WorkingTimeNotice;
};

/**
 * What a duplicate is seeded from — the parts of a notice that are copied. A {@link WorkingTimeNotice} satisfies it,
 * and a notice request is mapped to it, so both can be duplicated.
 */
export type DuplicateWorkingTimeNoticeSource = Pick<WorkingTimeNotice, "userId" | "userFullname" | "workingTimeTypeId" | "description" | "start" | "end">;

/**
 * Maps a notice request to what its duplicate is seeded from.
 */
export function noticeRequestDuplicateSource(request: WorkingTimeNoticeRequestResponseModel): DuplicateWorkingTimeNoticeSource {
	return {
		userId: request.user.id,
		userFullname: request.user.fullname,
		workingTimeTypeId: request.workingTimeType.id,
		description: request.description ?? undefined,
		start: request.start,
		end: request.end
	};
}

type DuplicateWorkingTimeNoticeDialogProps = WorkingTimeNoticeDialogCommonProps & {
	source: DuplicateWorkingTimeNoticeSource;
};

/**
 * The working time types a notice can be booked with directly. Editing a notice only switches among these, so
 * a notice never turns into a request; notices of {@code BY_REQUEST} types are created by an approved request
 * and are not edited directly (mirrors {@code WorkingTimeNoticeService.validateEditPermission}).
 */
function directlyBookableWorkingTimeTypes(workingTimeTypes: WorkingTimeType[]): WorkingTimeType[] {
	return workingTimeTypes.filter(wtt => wtt.noticeMode !== WorkingTimeNoticeMode.BY_REQUEST);
}

/**
 * Builds the form view-model from an existing notice. Shared by the editable and read-only renderings
 * of {@link EditWorkingTimeNoticeDialog} — both seed identically; only the footer, title, and
 * editability differ.
 */
function noticeInitialForm(notice: WorkingTimeNotice): WorkingTimeNoticeForm {
	return {
		id: notice.id,
		version: notice.version,
		selectedUser: {id: notice.userId, fullname: notice.userFullname},
		selectedWorkingTimeType: {
			id: notice.workingTimeTypeId,
			name: notice.workingTimeTypeName,
			category: notice.workingTimeTypeCategory,
			noticeMode: notice.noticeMode
		},
		description: notice.description ?? "",
		start: parseISODate(notice.start),
		end: parseISODate(notice.end)
	};
}

/**
 * Builds the form view-model for a copy of a notice, used by {@link DuplicateWorkingTimeNoticeDialog}. It carries no
 * `id`/`version`, so saving creates a new notice (or notice request) instead of updating the original.
 * <ul>
 *   <li>The working time type is only taken over when the current user may author it (it is in
 *       `workingTimeTypes`); otherwise it is left empty and the select preselects the first type it offers.
 *       It is taken from that list rather than from the source, so its `noticeMode` is the current one and
 *       decides whether the copy is saved as a notice or as a notice request.</li>
 *   <li>The user is only taken over when the user row is shown; otherwise it is left empty like in the create
 *       variant, so the backend defaults it to the current user.</li>
 * </ul>
 */
function duplicateInitialForm(source: DuplicateWorkingTimeNoticeSource, workingTimeTypes: WorkingTimeType[], showUserRow: boolean): WorkingTimeNoticeForm {
	const workingTimeType = workingTimeTypes.find(wtt => wtt.id === source.workingTimeTypeId);
	return {
		selectedUser: showUserRow ? {id: source.userId, fullname: source.userFullname} : null,
		selectedWorkingTimeType: workingTimeType
			? {id: workingTimeType.id, name: workingTimeType.name, category: workingTimeType.category, noticeMode: workingTimeType.noticeMode}
			: null,
		description: source.description ?? "",
		start: parseISODate(source.start),
		end: parseISODate(source.end)
	};
}

/**
 * Decides whether the edit dialog is view-only. The single place the rule is decided; the result feeds
 * both the controller (field disabling) and the variant's footer/title composition below.
 * <p>
 * Three reasons make a notice view-only:
 * <ol>
 *   <li>The backend reports it as {@code balanced} — nobody, not even an admin, may still change it, so the
 *       dialog opens read-only rather than failing on save.</li>
 *   <li>Its working time type is not one the current user can pick (archived / off their selectable
 *       {@code workingTimeTypes} / requiring a request, see {@link directlyBookableWorkingTimeTypes}) — it can
 *       be displayed but not re-authored.</li>
 *   <li>The current user is not permitted to edit it. This mirrors the backend
 *       {@code WorkingTimeNoticeService.validateEditPermission}: an {@code ALLOWED} notice may be edited by
 *       its owner or by a leader of its owner; a {@code NOT_ALLOWED} notice only by a leader of its
 *       owner; admins always. "Leader of its owner" is resolved with the same idiom the other entry
 *       types use ({@code isGroupLeaderForThemself} for the user's own notice — i.e. am I a leader of a
 *       team I belong to — and the coarse {@code globals.isGroupLeader} for other users, whose precision
 *       is provided by the role-scoped calendar query and the backend check).</li>
 * </ol>
 */
function isNoticeReadOnly(notice: WorkingTimeNotice, workingTimeTypes: WorkingTimeType[], showUserRow: boolean, isGroupLeaderForThemself: boolean): boolean {
	if (notice.balanced) {
		return true;
	}
	if (!workingTimeTypes.some(wtt => wtt.id === notice.workingTimeTypeId)) {
		return true;
	}
	if (globals.isAuthorizedForAllUsers) {
		return false;
	}
	if (notice.userId === globals.userId) {
		// Own notice: an ALLOWED notice is editable by its owner; a NOT_ALLOWED one only if the owner is a
		// leader of a team they belong to (mirrors validateEditPermission for the self case).
		return notice.noticeMode === WorkingTimeNoticeMode.NOT_ALLOWED && !isGroupLeaderForThemself;
	}
	// Another user's notice: editable only where the calendar is role-scoped to users the current user can
	// manage (the approval page, showUserRow=true) and the user is a leader. On the personal page
	// (showUserRow=false) the team view also surfaces peers the user does not lead, so others are view-only
	// there — the precise per-user leadership check stays on the backend.
	return !(showUserRow && globals.isGroupLeader);
}

/**
 * Whether the revision history may be requested for this notice. Mirrors the backend's view permission
 * ({@code DomainObjectOfUserVoter} on the loaded notice): own notice, admin, or leader of its owner.
 * Deliberately <em>not</em> {@code isNoticeReadOnly} — a balanced or off-list notice is read-only yet its
 * history is still the viewer's to see.
 * <p>
 * The team calendar on the personal page also surfaces plain teammates, who may see the entry but not its
 * history; without this gate the dialog would fire a request the backend rejects.
 */
function mayViewNoticeHistory(notice: WorkingTimeNotice, showUserRow: boolean): boolean {
	return globals.isAuthorizedForAllUsers
		|| notice.userId === globals.userId
		|| (showUserRow && globals.isGroupLeader);
}

/**
 * Returns a counter that increments every time {@code open} transitions from {@code false} to
 * {@code true} — i.e. once per (re)open.
 *
 * It exists to feed the {@code key} of {@link WorkingTimeNoticeDialogInstance}. The dialog instance
 * stays mounted while closed (the modal keeps it mounted to play its exit animation), so its form
 * state would otherwise survive a close. Folding this id into the key makes every reopen produce a
 * distinct key, which remounts the instance and resets the form to its initial values.
 *
 * The notice identity ({@code id}/{@code version}) in the key is not sufficient on its own:
 * <ul>
 *   <li>the create dialog has no identity at all, so its key would be constant; and</li>
 *   <li>reopening the same notice after cancelling edits keeps the same {@code id}/{@code version}
 *       (version only changes on save), so abandoned edits would reappear.</li>
 * </ul>
 * The session id covers both: it changes on every open regardless of identity.
 *
 * The increment is computed <em>during render</em> (React's "adjust state when a prop changes"
 * pattern), not in an effect. An effect would bump the id only after the instance had already
 * mounted with the previous id, so opening the dialog would mount it once, then immediately remount
 * it with the new key — firing every mount-time data fetch (e.g. the user list) twice, the first
 * aborted. Detecting the transition in render gives the instance its final key on the first mount.
 *
 * @see https://react.dev/reference/react/useState#storing-information-from-previous-renders
 * @see https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
 */
function useOpenSessionId(open: boolean): number {
	const [openSessionId, setOpenSessionId] = useState(0);
	const [wasOpen, setWasOpen] = useState(open);

	if (open !== wasOpen) {
		setWasOpen(open);
		if (open) {
			setOpenSessionId(id => id + 1);
		}
	}

	return openSessionId;
}

type WorkingTimeNoticeDialogInstanceProps = WorkingTimeNoticeDialogControllerOptions & {
	dialogTitle: string;
	history?: React.ReactNode;
	// Footer actions, composed per variant (edit adds a delete button).
	children: React.ReactNode;
};

/**
 * Owns the controller for one dialog and provides it to the subtree. The variants render this with a
 * {@code key} derived from the notice identity / open-session, so a change of identity remounts it —
 * which is what resets the form (fresh {@code useState}), replacing any imperative re-initialization.
 */
function WorkingTimeNoticeDialogInstance({dialogTitle, history, children, ...controllerOptions}: WorkingTimeNoticeDialogInstanceProps) {
	const controller = useWorkingTimeNoticeDialogController(controllerOptions);
	return (
		<WorkingTimeNoticeDialogProvider value={controller}>
			<WorkingTimeNoticeDialogShell dialogTitle={dialogTitle} history={history}>
				{children}
			</WorkingTimeNoticeDialogShell>
		</WorkingTimeNoticeDialogProvider>
	);
}

type CreateWorkingTimeNoticeDialogProps = WorkingTimeNoticeDialogCommonProps & {
	initialValues?: CreateWorkingTimeNoticeInitialValues | null;
};

export function CreateWorkingTimeNoticeDialog(props: CreateWorkingTimeNoticeDialogProps) {
	const openSessionId = useOpenSessionId(props.open);
	// Keeping the dialog open after saving to create a further notice
	const [createAnother, setCreateAnother] = useState(false);
	const today = Temporal.Now.plainDateISO();
	const start = props.initialValues?.start ?? today;
	const end = props.initialValues?.end ?? start;
	return (
		<WorkingTimeNoticeDialogInstance
			key={`create:${openSessionId}`}
			{...props}
			readOnly={false}
			userSelectable={true}
			initialForm={{
				selectedUser: props.initialValues?.selectedUser ?? (props.showUserRow
					? {id: globals.userId, fullname: globals.userFullname}
					: null),
				// The select preselects the working time type it offers first and reports it back, so the
				// dialog does not need to know the category order its items are grouped by.
				selectedWorkingTimeType: null,
				description: "",
				start,
				end
			}}
			dialogTitle={messages["workingTimeNotice.dialog.title"]}
			createAnother={createAnother}
			onClose={() => {
				setCreateAnother(false);
				props.onClose();
			}}
		>
			<CreateAnotherCheckbox checked={createAnother} onCheckedChange={setCreateAnother}/>
			<SaveAndApproveNoticeButton showUserRow={props.showUserRow} isGroupLeaderForThemself={props.isGroupLeaderForThemself}/>
			<SaveNoticeButton/>
			<CancelNoticeButton/>
		</WorkingTimeNoticeDialogInstance>
	);
}

export function EditWorkingTimeNoticeDialog({
												notice,
												...props
											}: EditWorkingTimeNoticeDialogProps) {
	const openSessionId = useOpenSessionId(props.open);
	const workingTimeTypes = directlyBookableWorkingTimeTypes(props.workingTimeTypes);
	// Read-only is derived once here (not in the bridge, not re-derived in the controller) and drives
	// three explicit, composed differences: the title, the footer actions, and field editability.
	const readOnly = isNoticeReadOnly(notice, workingTimeTypes, props.showUserRow, props.isGroupLeaderForThemself);
	return (
		<WorkingTimeNoticeDialogInstance
			key={`edit:${openSessionId}:${notice.id}:${notice.version}`}
			{...props}
			workingTimeTypes={workingTimeTypes}
			readOnly={readOnly}
			userSelectable={false}
			initialForm={noticeInitialForm(notice)}
			dialogTitle={messages[readOnly
				? "workingTimeNotice.dialog.title.show"
				: "workingTimeNotice.dialog.title.edit"]}
			history={mayViewNoticeHistory(notice, props.showUserRow) ? <WorkingTimeNoticeRevisions noticeId={notice.id}/> : undefined}
		>
			{readOnly ? (
				// View-only: the only action is to dismiss. No Save/Delete to compose — so those buttons
				// stay pure (no self-hiding `meta.readOnly` checks); their absence is structural here.
				<CancelNoticeButton/>
			) : (
				<>
					<DeleteNoticeButton/>
					<SaveNoticeButton/>
					<CancelNoticeButton/>
				</>
			)}
		</WorkingTimeNoticeDialogInstance>
	);
}

/**
 * Creates a new notice (or notice request) seeded from an existing one. Behaves like the create variant (editable,
 * user selectable), but without the "create another" checkbox, the revision history, or a delete action.
 */
export function DuplicateWorkingTimeNoticeDialog({
													 source,
													 ...props
												 }: DuplicateWorkingTimeNoticeDialogProps) {
	const openSessionId = useOpenSessionId(props.open);
	return (
		<WorkingTimeNoticeDialogInstance
			key={`duplicate:${openSessionId}`}
			{...props}
			readOnly={false}
			userSelectable={true}
			initialForm={duplicateInitialForm(source, props.workingTimeTypes, props.showUserRow)}
			dialogTitle={messages["workingTimeNotice.dialog.title.duplicate"]}
		>
			<SaveAndApproveNoticeButton showUserRow={props.showUserRow} isGroupLeaderForThemself={props.isGroupLeaderForThemself}/>
			<SaveNoticeButton/>
			<CancelNoticeButton/>
		</WorkingTimeNoticeDialogInstance>
	);
}

// Footer action buttons. Each reads the shared controller from context, so a variant composes the
// footer it needs simply by listing the buttons — the delete button's presence is a structural fact
// of the edit variant, not a runtime check inside a shared button.
function DeleteNoticeButton() {
	const {actions} = useWorkingTimeNoticeDialogContext();
	return (
		<button type="button" className="btn btn-outline-danger pull-left" onClick={actions.deleteNotice}>
			{messages["dialog.delete"]}
		</button>
	);
}

function CancelNoticeButton() {
	const {actions} = useWorkingTimeNoticeDialogContext();
	return (
		<button type="button" className="btn btn-default" onClick={actions.close}>
			{messages["dialog.cancel"]}
		</button>
	);
}

function SaveNoticeButton() {
	const {state, actions} = useWorkingTimeNoticeDialogContext();
	return (
		<button type="button" className="btn btn-primary" onClick={actions.save} disabled={state.saving}>
			{state.saving && <i className="far fa-spinner-third spinner ml-5 mr-5"/>}
			{isNoticeRequestForm(state.form) ? messages["dialog.request"] : messages["dialog.save"]}
		</button>
	);
}

interface SaveAndApproveNoticeButtonProps {
	showUserRow: boolean;
	isGroupLeaderForThemself: boolean
}

/**
 * Creates the notice request already approved, like the absence dialog offers it for working time requests. Shown
 * only in request mode and to administrators or team leaders allowed to approve requests immediately.
 */
function SaveAndApproveNoticeButton({showUserRow, isGroupLeaderForThemself}: SaveAndApproveNoticeButtonProps) {
	const {state, actions} = useWorkingTimeNoticeDialogContext();
	const selectedUserId = state.form.selectedUser?.id ?? globals.userId;
	if (!isNoticeRequestForm(state.form) || !permissionForSaveAndApproveButton(showUserRow, selectedUserId, isGroupLeaderForThemself)) {
		return null;
	}
	return (
		<button type="button" className="btn btn-primary" onClick={actions.saveAndApprove} disabled={state.saving}>
			{state.saving && <i className="far fa-spinner-third spinner ml-5 mr-5"/>}
			{messages["dialog.saveAndApprove"]}
		</button>
	);
}

interface WorkingTimeNoticeDialogShellProps {
	dialogTitle: string;
	// Rendered below the form; the edit variant passes the revision history, create passes nothing.
	history?: React.ReactNode;
	// The footer actions are composed per variant (e.g. edit adds a delete button) and passed as children.
	children: React.ReactNode;
}

function WorkingTimeNoticeDialogShell({dialogTitle, history, children}: WorkingTimeNoticeDialogShellProps) {
	const {state, actions, meta} = useWorkingTimeNoticeDialogContext();
	const {workingTimeTypes, readOnly} = meta;
	const {open, form, fieldErrors} = state;
	const workingTimeTypeSelectId = useId();
	const descriptionId = useId();
	// The selected type's category drives the dialog's accent border. Read from the form's selected
	// type (not a lookup in `workingTimeTypes`) so it is correct even for an off-list, read-only type.
	const selectedCategory = form.selectedWorkingTimeType?.category;
	const contentClassName = selectedCategory ? `border-${selectedCategory}` : undefined;

	/*
	 * When read-only the selected type may be off the user's selectable list, so a select
	 * over `workingTimeTypes` would have no item matching the value and show its raw id.
	 * Render the form's own selected type as the sole item instead; the disabled select
	 * then displays the real type. (Editable: it is always in the list.)
	 */
	const workingTimeTypeOptions = readOnly && form.selectedWorkingTimeType
		? [form.selectedWorkingTimeType]
		: workingTimeTypes;

	return (
		// `enforceFocus={false}`: the working time type select renders its popup in a portal outside the
		// dialog, and the focus trap would pull focus straight back out of it, leaving the popup
		// keyboard-dead.
		<Modal
			show={open}
			onHide={actions.close}
			size="modal-lg"
			contentClassName={contentClassName}
			enforceFocus={false}
		>
			<Modal.Header closeButton>
				<Modal.Title>{dialogTitle}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="record-dialog">
					<form className="form-horizontal">
						<div className="row">
							<div className="col-lg-7">
								{meta.showUserRow && (
									<WorkingTimeNoticeUserComboBoxFormGroup
										value={form.selectedUser}
										onValueChange={actions.setSelectedUser}
										disabled={readOnly || !meta.userSelectable}
									/>
								)}

								<FormGroup
									label={messages["workingTimeNotice.workingTimeType"]}
									htmlFor={workingTimeTypeSelectId}
									labelColClass="col-xs-3"
									inputColClass="col-xs-9"
									error={fieldErrors["workingTimeTypeId"]}
								>
									<WorkingTimeTypeSelect
										id={workingTimeTypeSelectId}
										value={form.selectedWorkingTimeType?.id ?? null}
										workingTimeTypes={workingTimeTypeOptions}
										onValueChange={actions.setWorkingTimeTypeId}
										disabled={readOnly}
									/>
								</FormGroup>

								<FormGroup
									label={messages["workingTimeNotice.description"]}
									htmlFor={descriptionId}
									labelColClass="col-xs-3"
									inputColClass="col-xs-9"
									error={fieldErrors["description"]}
								>
										<textarea
											id={descriptionId}
											className="form-control"
											rows={6}
											maxLength={10000}
											value={form.description ?? ""}
											onChange={e => actions.setDescription(e.target.value)}
											disabled={readOnly}
										/>
								</FormGroup>
							</div>

							<div className="col-lg-5">
								<DatePickerFormGroup
									label={messages["workingTimeNotice.start"]}
									value={form.start}
									onValueChange={actions.setStartDate}
									labelColClass="col-xs-3"
									inputColClass="col-xs-9"
									error={fieldErrors["start"]}
									disabled={readOnly}
								/>

								<DatePickerFormGroup
									label={messages["workingTimeNotice.end"]}
									value={form.end}
									onValueChange={actions.setEndDate}
									labelColClass="col-xs-3"
									inputColClass="col-xs-9"
									error={fieldErrors["end"]}
									disabled={readOnly}
								/>
							</div>
						</div>
					</form>
					{history}
				</div>
			</Modal.Body>
			<Modal.Footer>
				{children}
			</Modal.Footer>
		</Modal>
	);
}
